import React, { useMemo, useEffect, useReducer, useState } from 'react';
import { useTable, usePagination, Column, CellProps, Row, Cell } from 'react-table';
import { Ticket } from '../utils/commandCenterTypes';
import { formatTaskTypeLabel } from '../utils/taskTypes';
import { normalizeTicketStatus } from '../utils/ticketFilters';
import { resolveProfilePictureUrl } from '../utils/profilePicture';
import {
  CommandCentreInstanceUser,
  listInstanceUsers,
  listInternalUsers,
} from '../../../api/instances';
import '../TicketTable/TicketTable.css';

const timeAgo = (timestamp: string | number | Date): string => {
  const now = new Date().getTime();
  const then = new Date(timestamp).getTime();
  const diff = Math.floor((now - then) / 1000);
  if (diff < 60) return `${diff} second${diff !== 1 ? 's' : ''} ago`;
  const minutes = Math.floor(diff / 60);
  if (minutes < 60) return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days} day${days !== 1 ? 's' : ''} ago`;
  const weeks = Math.floor(days / 7);
  if (weeks < 4) return `${weeks} week${weeks !== 1 ? 's' : ''} ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months} month${months !== 1 ? 's' : ''} ago`;
  const years = Math.floor(days / 365);
  return `${years} year${years !== 1 ? 's' : ''} ago`;
};

const URGENCY_LABELS: Record<string, { text: string; className: string; icon?: string }> = {
  minor: { text: 'Minor', className: 'urgency-minor' },
  major: { text: 'Major', className: 'urgency-major' },
  emergency: { text: 'Emergency', className: 'urgency-emergency', icon: 'fa-bell' },
};

const STATUS_LABELS: Record<string, { text: string; className: string }> = {
  open: { text: 'Open', className: 'status-open' },
  'in-progress': { text: 'In Progress', className: 'status-in-progress' },
  'on-hold': { text: 'On Hold', className: 'status-on-hold' },
  completed: { text: 'Completed', className: 'status-completed' },
};

const getUserName = (user?: CommandCentreInstanceUser | null) => {
  if (!user) return 'Unknown user';
  const name = `${user.first_name || ''} ${user.last_name || ''}`.trim();
  return name || user.email_address || user.email || `User #${user.id}`;
};

const getInitials = (name: string) => {
  const parts = name.split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  return parts.slice(0, 2).map((part) => part[0]?.toUpperCase()).join('') || '?';
};

interface TicketTableProps {
  tickets: Ticket[];
  onSelectTicket: (ticket: Ticket) => void;
  headerBgColor?: string;
  loading?: boolean;
  recentlyUpdatedTicketId?: number | null;
}

const TicketTable: React.FC<TicketTableProps> = ({
  tickets,
  onSelectTicket,
  headerBgColor,
  loading = false,
  recentlyUpdatedTicketId,
}) => {
  const [, forceUpdate] = useReducer((x) => x + 1, 0);
  const [assigneeUsers, setAssigneeUsers] = useState<CommandCentreInstanceUser[]>([]);

  useEffect(() => {
    const interval = setInterval(forceUpdate, 60000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadAssigneeUsers = async () => {
      const instanceIds = [...new Set(tickets.map((ticket) => Number(ticket.instanceId)).filter(Boolean))];
      const [internalUsers, scopedUsers] = await Promise.all([
        listInternalUsers().catch(() => []),
        instanceIds.length === 1
          ? listInstanceUsers(instanceIds[0]).then((result) => result.users).catch(() => [])
          : Promise.resolve([]),
      ]);

      if (cancelled) return;

      const byId = new Map<number, CommandCentreInstanceUser>();
      [...internalUsers, ...scopedUsers].forEach((user) => byId.set(Number(user.id), user));
      setAssigneeUsers([...byId.values()]);
    };

    loadAssigneeUsers();
    return () => {
      cancelled = true;
    };
  }, [tickets]);

  const assigneeUserMap = useMemo(
    () => new Map(assigneeUsers.map((user) => [Number(user.id), user])),
    [assigneeUsers]
  );

  const columns = useMemo<Column<Ticket>[]>(
    () => [
      {
        Header: 'Client',
        accessor: 'sender_name',
        Cell: ({ row }: CellProps<Ticket>) => {
          const ticket = row.original;
          const avatarUrl = resolveProfilePictureUrl(ticket.sender_profile_picture);
          return (
            <div className="ticket-client-cell">
              {avatarUrl ? (
                <img
                  src={avatarUrl}
                  alt={ticket.sender_name || ''}
                  className="ticket-client-avatar"
                />
              ) : (
                <div className="ticket-client-avatar placeholder">
                  {ticket.sender_name ? ticket.sender_name[0] : 'N/A'}
                </div>
              )}
              <div className="ticket-client-info">
                <span className="ticket-client-name">{ticket.sender_name || 'Unknown'}</span>
                <span className="ticket-client-type">
                  {ticket.sender_type === 'instance' ? (
                    <i className="fa fa-building" title="Instance" />
                  ) : (
                    <i className="fa fa-user" title="User" />
                  )}
                </span>
              </div>
            </div>
          );
        },
      },
      {
        Header: 'Task Name',
        accessor: 'task_name',
        Cell: ({ value }: CellProps<Ticket>) => <span>{(value as string) || 'Unknown'}</span>,
      },
      {
        Header: 'Assigned',
        accessor: 'assignees',
        Cell: ({ value }: CellProps<Ticket>) => {
          const assignees = Array.isArray(value) ? value : [];
          if (!assignees.length) {
            return <span className="ticket-assigned-empty">—</span>;
          }

          return (
            <div className="ticket-assigned-stack" aria-label="Assigned users">
              {assignees.slice(0, 4).map((assignee) => {
                const user = assigneeUserMap.get(Number(assignee.id));
                const name = getUserName(user);
                const avatarUrl = resolveProfilePictureUrl(
                  user?.profilePicture ?? user?.profile_picture
                );

                return avatarUrl ? (
                  <img
                    key={assignee.id}
                    className="ticket-assigned-avatar"
                    src={avatarUrl}
                    alt=""
                    title={name}
                  />
                ) : (
                  <span
                    key={assignee.id}
                    className="ticket-assigned-avatar ticket-assigned-avatar--fallback"
                    title={name}
                  >
                    {getInitials(name)}
                  </span>
                );
              })}
              {assignees.length > 4 ? (
                <span className="ticket-assigned-more" title={`${assignees.length - 4} more assignees`}>
                  +{assignees.length - 4}
                </span>
              ) : null}
            </div>
          );
        },
      },
      {
        Header: 'Urgency',
        accessor: 'task_urgency',
        Cell: ({ value }: CellProps<Ticket>) => {
          const key = String(value ?? '').trim().toLowerCase();
          const info =
            URGENCY_LABELS[key] || { text: (value as string) || 'Unknown', className: '' };
          return (
            <span className={`ticket-badge ${info.className}`}>
              {info.icon && <i className={`fa ${info.icon}`} style={{ marginRight: 4 }} />}
              {info.text}
            </span>
          );
        },
      },
      {
        Header: 'Type',
        accessor: 'task_type',
        Cell: ({ value }: CellProps<Ticket>) => {
          return <span>{formatTaskTypeLabel(value as string)}</span>;
        },
      },
      {
        Header: 'Status',
        accessor: 'task_status',
        Cell: ({ value }: CellProps<Ticket>) => {
          const normalized = normalizeTicketStatus(value as string);
          const info =
            STATUS_LABELS[normalized] || { text: String(value || 'Unknown'), className: '' };

          return <span className={`ticket-badge ${info.className}`}>{info.text}</span>;
        },
      },
      {
        Header: 'Last Update',
        accessor: 'updatedAt',
        Cell: ({ row, value }: CellProps<Ticket>) => {
          const isRecent = recentlyUpdatedTicketId != null && row.original.id === recentlyUpdatedTicketId;

          return (
            <span
              className={
                isRecent
                  ? 'ticket-last-update ticket-last-update--recent'
                  : 'ticket-last-update'
              }
            >
              {isRecent ? 'Just updated' : timeAgo(value)}
              {isRecent && (
                <i className="fa fa-bell ticket-last-update-bell" aria-hidden="true" />
              )}
            </span>
          );
        },
      },
    ],
    [assigneeUserMap, recentlyUpdatedTicketId]
  );

  const tableInstance = useTable<Ticket>(
    {
      columns,
      data: tickets,
      initialState: {
        pageIndex: 0,
        pageSize: Math.min(50, Math.max(10, tickets.length || 10)),
      },
    },
    usePagination
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    rows,
    canPreviousPage,
    canNextPage,
    pageOptions,
    nextPage,
    previousPage,
    gotoPage,
    setPageSize,
    state: { pageIndex, pageSize },
  } = tableInstance;

  useEffect(() => {
    if (pageIndex > pageOptions.length - 1) gotoPage(0);
  }, [gotoPage, pageIndex, pageOptions.length, tickets.length]);

  return (
    <div className="core-command-table-wrapper">
      <table className="core-command-table maintenance" {...getTableProps()}>
        <thead>
          {headerGroups.map((headerGroup) => (
            <tr
              {...headerGroup.getHeaderGroupProps()}
              style={{ backgroundColor: headerBgColor, color: 'white' }}
            >
              {headerGroup.headers.map((column, index) => (
                <th {...column.getHeaderProps()} style={{ backgroundColor: headerBgColor }} key={index}>
                  {column.render('Header')}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()}>
          {loading && (
            <tr>
              <td colSpan={columns.length} className="ticket-table-loading-cell">
                <div className="sk-chase">
                  <div className="sk-chase-dot"></div>
                  <div className="sk-chase-dot"></div>
                  <div className="sk-chase-dot"></div>
                  <div className="sk-chase-dot"></div>
                  <div className="sk-chase-dot"></div>
                  <div className="sk-chase-dot"></div>
                </div>
                <div className="ticket-table-loading-text">Loading tasks...</div>
              </td>
            </tr>
          )}

          {!loading && page.length === 0 && (
            <tr>
              <td colSpan={columns.length} style={{ textAlign: 'center', padding: '16px' }}>
                No tickets to display.
              </td>
            </tr>
          )}

          {!loading &&
            page.map((row: Row<Ticket>) => {
              prepareRow(row);
              const isRecent = recentlyUpdatedTicketId != null && row.original.id === recentlyUpdatedTicketId;
              return (
                <tr
                  {...row.getRowProps()}
                  onClick={() => onSelectTicket(row.original)}
                  style={{ cursor: 'pointer' }}
                  className={isRecent ? 'ticket-row--recent-update' : undefined}
                >
                  {row.cells.map((cell: Cell<Ticket>) => (
                    <td {...cell.getCellProps()}>{cell.render('Cell')}</td>
                  ))}
                </tr>
              );
            })}
        </tbody>
      </table>

      <div className="table-pagination">
        <div className="table-pagination__info">
          Showing <strong>{page.length}</strong> of <strong>{rows.length}</strong>
        </div>

        <div className="table-pagination__controls">
          <button onClick={() => gotoPage(0)} disabled={!canPreviousPage} title="First">
            {'<<'}
          </button>
          <button onClick={() => previousPage()} disabled={!canPreviousPage} title="Previous">
            {'<'}
          </button>
          <span className="table-pagination__page">
            Page <strong>{pageIndex + 1}</strong> of <strong>{pageOptions.length || 1}</strong>
          </span>
          <button onClick={() => nextPage()} disabled={!canNextPage} title="Next">
            {'>'}
          </button>
          <button
            onClick={() => gotoPage(pageOptions.length - 1)}
            disabled={!canNextPage}
            title="Last"
          >
            {'>>'}
          </button>

          <label className="table-pagination__size">
            Rows per page:{' '}
            <select
              value={pageSize}
              onChange={(event) => {
                const value = event.target.value;
                if (value === 'all') {
                  setPageSize(rows.length || 1);
                } else {
                  setPageSize(Number(value));
                }
              }}
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
              <option value="all">All</option>
            </select>
          </label>
        </div>
      </div>
    </div>
  );
};

export default TicketTable;
